import { useState } from 'react';
import { get } from 'lodash';
import { useSnackbar } from 'notistack';
import { Credit } from 'lib/types/credits';
import { useApi } from 'lib/contexts/ApplicationState';
import { User } from 'lib/types/users';
import { Role } from 'lib/types';
import { EditCreditForm } from './EditCreditForm';

interface Props {
  postId: number;
  credit: Credit;
  credits: Credit[];
  onChange: (message?: string) => void;
  onCancel: () => void;
}

export const CreditEdit = ({
  postId,
  credit,
  credits,
  onChange,
  onCancel,
}: Props) => {
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();

  const [isSaving, setIsSaving] = useState(false);

  const removeCredit = async () => {
    setIsSaving(true);
    try {
      if (credit?.id) {
        await API.deleteCredit(credit.id);
        onChange('Credit has been removed.');
        onCancel();
      }
    } catch (error) {
      const msg = get(error, 'message');
      enqueueSnackbar(`There was an error deleting this credit: ${msg}`, { variant: 'error' });
    }
    setIsSaving(false);
  };

  const updateCredit = async (
    user: User, roles: Role[], isPrivate?: boolean,
  ) => {
    const roleId = roles[0].id || credit.roleId;
    const uc = {
      postId,
      roleId,
      userId: user.id,
      isPrivate,
    };

    try {
      await API.updateCredit(credit.id, uc);
      if (onChange) onChange('Credit has been updated.');
      onCancel();
    } catch (e) {
      const msg = get(e, 'message');
      enqueueSnackbar(`There was an error updating the credit: ${msg}`, { variant: 'error' });
    }
  };

  if (isSaving) return <div className="w-full text-center">Saving...</div>;

  return (
    <div className="border-b">
      <EditCreditForm
        postId={postId}
        credits={credits}
        credit={credit}
        saveLabel="Save Credit"
        onSave={updateCredit}
        onCancel={onCancel}
        onRemove={removeCredit}
      />
    </div>
  );
};
